import { useState } from 'react';
import { useEditorStore } from '../store/editor-store';
import { IconGallery, IconUpload } from './Icons';
import { SizeGuideModal } from './SizeGuideModal';
import { PrintServiceOptions } from './PrintServiceOptions';
import { PrintMethodOptions } from './PrintMethodOptions';
import { swatchStyle } from '../lib/swatchStyle';
import {
  modelColorAxisLabel,
  modelShowsColors,
  modelShowsSizes,
  modelSizeAxisLabel,
  pluralizeAxisLabel,
} from '../lib/modelAxes';
import type { CatalogProvider } from '@ox/schema';

function IconTextMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 6h14M12 6v13M8 19h8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconShirt({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 4l-4 3v4l3-1v9h10V10l3 1V7l-4-3-2 2h-2L8 4z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Left home panel when nothing is selected (La Tostadora-style).
 */
export function HomePanel({
  onUploadClick,
  catalog,
}: {
  onUploadClick: () => void;
  catalog: CatalogProvider;
}) {
  const model = useEditorStore((s) => s.model);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const setColor = useEditorStore((s) => s.setColor);
  const setTool = useEditorStore((s) => s.setTool);
  const setPanel = useEditorStore((s) => s.setPanel);
  const color = model?.colors.find((c) => c.key === colorKey) ?? model?.colors[0];
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const sizeGuideUrl = model?.sizeGuideUrl?.trim();
  const hasSizeGuide = !!sizeGuideUrl && sizeGuideUrl !== '#';
  const showSizes = modelShowsSizes(model);
  const showColors = modelShowsColors(model);
  const sizeAxis = pluralizeAxisLabel(modelSizeAxisLabel(model));
  const colorAxis = modelColorAxisLabel(model);

  return (
    <aside className="ox-side-panel ox-home-panel">
      <div className="ox-home-actions">
        <button
          type="button"
          className="ox-home-action"
          onClick={() => {
            setTool('text');
            setPanel('addText');
          }}
        >
          <span className="ox-home-action-ico">
            <IconTextMark />
          </span>
          Añadir texto
        </button>
        <button type="button" className="ox-home-action" onClick={onUploadClick}>
          <span className="ox-home-action-ico">
            <IconUpload size={22} />
          </span>
          Cargar imagen
        </button>
        <button
          type="button"
          className="ox-home-action"
          onClick={() => {
            setPanel('gallery');
          }}
        >
          <span className="ox-home-action-ico">
            <IconGallery size={22} />
          </span>
          Galería
        </button>
        <button
          type="button"
          className="ox-home-action"
          onClick={() => {
            setTool('products');
            setPanel('products');
          }}
        >
          <span className="ox-home-action-ico">
            <IconShirt />
          </span>
          Cambiar producto
        </button>
      </div>

      <PrintMethodOptions catalog={catalog} />
      <PrintServiceOptions />

      {model && (showSizes || showColors) && (
        <div className="ox-home-meta">
          {showSizes && (
            <div className="ox-home-sizes">
              <span>
                {sizeAxis}: <strong>{model.sizeRangeLabel}</strong>
              </span>
              {hasSizeGuide && (
                <button
                  type="button"
                  className="ox-home-link"
                  onClick={() => setSizeGuideOpen(true)}
                >
                  Guía de tallas
                </button>
              )}
            </div>
          )}
          {showColors && (
            <div className="ox-home-color">
              <p>
                {colorAxis}: <strong>{color?.label ?? colorKey}</strong>
              </p>
              <div className="ox-swatch-grid">
                {model.colors.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`ox-swatch-btn${colorKey === c.key ? ' active' : ''}`}
                    style={swatchStyle(c.hex)}
                    title={c.label}
                    aria-label={c.label}
                    onClick={() => setColor(c.key)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {sizeGuideOpen && hasSizeGuide && sizeGuideUrl && (
        <SizeGuideModal url={sizeGuideUrl} onClose={() => setSizeGuideOpen(false)} />
      )}
    </aside>
  );
}
